import React, { useEffect, useState, useRef, createElement } from 'react';
import { MapPinIcon, DollarSignIcon, ShoppingCartIcon, ClockIcon, TrendingUpIcon, UsersIcon, CheckCircleIcon, AlertCircleIcon, TruckIcon, BellIcon, XCircleIcon, CalendarIcon, FilterXIcon, DownloadIcon, CheckIcon, BanIcon, PackageIcon } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Card, CardHeader } from '../components/ui/Card';
import { StatsCard } from '../components/ui/StatsCard';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { Visit } from '../types';
const LOGO_URL = "/Captura_de_pantalla_2026-02-19_124313-removebg-preview.png";
async function getLogoBase64(): Promise<string | null> {
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject();
      img.src = LOGO_URL;
    });
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth || 100;
    canvas.height = img.naturalHeight || 100;
    const ctx = canvas.getContext('2d')!;
    ctx.drawImage(img, 0, 0);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}
export function DashboardPage() {
  const {
    user
  } = useAuth();
  const {
    visits,
    getTodayStats,
    users,
    orders,
    refreshData,
    newOrderAlert,
    totalVendido: totalVendidoCtx,
    totalCobrado: totalCobradoCtx,
    cobradoPorVendedor,
    totalVisitas: totalVisitasCtx,
    visitasPorVendedor,
    isLoading: appLoading
  } = useApp();
  // Date range filter for charts
  const [chartDateFrom, setChartDateFrom] = useState('');
  const [chartDateTo, setChartDateTo] = useState('');
  // ── Retroactive discount map (same logic as VisitsPage) ──────────────
  const [retroDiscountMap, setRetroDiscountMap] = useState<Record<string, number>>({});
  useEffect(() => {
    const timer = setTimeout(() => {
      const computeRetroDiscounts = async () => {
        const cobroVisits = visits.filter(v => (v.tipoVisita === 'cobro' || v.tipoVisita === 'cobro_y_venta') && v.cobro.huboCobo && v.cobro.montoCobrado > 0 && (v.cobro.descuento || 0) === 0 && !(v.cobro as any)?.anulado);
        if (cobroVisits.length === 0) {
          setRetroDiscountMap({});
          return;
        }
        const visitFactMap: Record<string, string[]> = {};
        const allNums: string[] = [];
        cobroVisits.forEach(v => {
          const fd: any[] = (v.cobro as any).facturasDetalle || [];
          const nums = fd.length > 0 ? fd.map((f: any) => f.numeroFactura).filter(Boolean) : v.cobro.numeroFactura ? v.cobro.numeroFactura.split(',').map((n: string) => n.trim()).filter(Boolean) : [];
          if (nums.length > 0) {
            visitFactMap[v.id] = nums;
            allNums.push(...nums);
          }
        });
        const uniqueNums = [...new Set(allNums)];
        if (uniqueNums.length === 0) return;
        try {
          const {
            data: invRows
          } = await supabaseAdmin.from('invoices').select('numero_factura, pp_pct, sub_total').in('numero_factura', uniqueNums.slice(0, 200));
          if (!invRows) return;
          const ppMap: Record<string, {
            ppPct: number | null;
            subTotal: number;
          }> = {};
          invRows.forEach((row: any) => {
            ppMap[row.numero_factura] = {
              ppPct: row.pp_pct != null ? Number(row.pp_pct) : null,
              subTotal: Number(row.sub_total) || 0
            };
          });
          const newMap: Record<string, number> = {};
          Object.entries(visitFactMap).forEach(([visitId, nums]) => {
            const v = visits.find(vis => vis.id === visitId);
            if (!v) return;
            const fd: any[] = (v.cobro as any).facturasDetalle || [];
            let totalDisc = 0;
            nums.forEach(num => {
              const pp = ppMap[num];
              const savedDesc = fd.find((f: any) => f.numeroFactura === num)?.descuento || 0;
              if (savedDesc === 0 && pp && pp.ppPct != null && pp.ppPct > 0 && pp.subTotal > 0) {
                totalDisc += Math.round(pp.subTotal * (pp.ppPct / 100) * 100) / 100;
              }
            });
            if (totalDisc > 0) newMap[visitId] = totalDisc;
          });
          setRetroDiscountMap(newMap);
        } catch {

          // Silently fail
        }
      };
      computeRetroDiscounts();
    }, 800);
    return () => clearTimeout(timer);
  }, [visits]);
  // Helper: get effective cobro amount (accounting for saved + retroactive discounts)
  const getEffectiveCobro = (visit: Visit): number => {
    if ((visit.cobro as any)?.anulado) return 0;
    if (!visit.cobro.huboCobo || visit.cobro.montoCobrado <= 0) return 0;
    const savedDesc = visit.cobro.descuento || 0;
    if (savedDesc > 0) {
      const credito = visit.cobro.credito || 0;
      if (credito > 0 && Math.abs(visit.cobro.montoCobrado - credito) < 0.01) {
        return Math.round((visit.cobro.montoCobrado - savedDesc) * 100) / 100;
      }
      return visit.cobro.montoCobrado;
    }
    const retroDisc = retroDiscountMap[visit.id] || 0;
    if (retroDisc > 0) {
      return Math.round((visit.cobro.montoCobrado - retroDisc) * 100) / 100;
    }
    return visit.cobro.montoCobrado;
  };
  const pendingOrders = orders.filter(o => o.estado === 'pendiente');
  const handleApproveOrder = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'procesando'
    }).eq('id', id);
    await refreshData();
  };
  const handleCancelOrder = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'cancelado'
    }).eq('id', id);
    await refreshData();
  };
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  // Permission helpers for dashboard sections (granular)
  const canSeeCollections = isAdmin || user?.permisos?.dashboard_cobros === true || user?.permisos?.dashboard_cobros === undefined;
  const canSeeSales = isAdmin || user?.permisos?.dashboard_ventas === true || user?.permisos?.dashboard_ventas === undefined;
  const canSeeVisits = isAdmin || user?.permisos?.dashboard_visitas === true || user?.permisos?.dashboard_visitas === undefined;
  const canSeeCharts = isAdmin || user?.permisos?.dashboard_graficos === true || user?.permisos?.dashboard_graficos === undefined;
  const canSeePedidos = isAdmin || user?.permisos?.dashboard_pedidos === true || user?.permisos?.dashboard_pedidos === undefined;
  const canSeeEquipo = isAdmin || user?.permisos?.dashboard_equipo === true;
  const canSeeProductividad = isAdmin || user?.permisos?.dashboard_productividad === true || user?.permisos?.dashboard_productividad === undefined;
  const canSeeEntregas = user?.permisos?.dashboard_entregas === true;
  const isRepartidor = canSeeEntregas && !isAdmin;
  const stats = getTodayStats(isAdmin ? undefined : user?.id);
  const recentVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id).slice(0, 6);
  const activeVendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  const formatTime = (date: Date | null) => {
    if (!date) return '--:--';
    return new Date(date).toLocaleTimeString('es-MX', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const getVisitTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      cobro: 'Cobro',
      venta: 'Venta',
      cobro_y_venta: 'Cobro y Venta',
      seguimiento: 'Seguimiento'
    };
    return labels[type] || type;
  };
  const getStatusBadge = (status: string) => {
    if (status === 'completada') return <Badge variant="success" data-id="element-2423" data-mp="1rkjivk3zrqh9">Completada</Badge>;
    if (status === 'en_progreso') return <Badge variant="warning" data-id="element-2424" data-mp="v0smzwlt15lt">En Progreso</Badge>;
    return <Badge variant="danger" data-id="element-2425" data-mp="z3fic7hop8nr">Cancelada</Badge>;
  };
  const allRelevantVisits = visits.filter(v => isAdmin || v.vendedorId === user?.id);
  // Filtered visits for charts based on date range
  const chartVisits = allRelevantVisits.filter(v => {
    if (chartDateFrom) {
      const visitDate = new Date(v.fecha);
      const fromDate = new Date(chartDateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (visitDate < fromDate) return false;
    }
    if (chartDateTo) {
      const visitDate = new Date(v.fecha);
      const toDate = new Date(chartDateTo);
      toDate.setHours(23, 59, 59, 999);
      if (visitDate > toDate) return false;
    }
    return true;
  });
  const hasChartFilter = chartDateFrom || chartDateTo;
  const cobrosBarData = isAdmin ? activeVendors.map(vendor => {
    const vendorVisits = chartVisits.filter(v => v.vendedorId === vendor.id);
    return {
      name: vendor.nombre.split(' ')[0],
      cobrado: vendorVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0),
      vendido: vendorVisits.reduce((sum, v) => sum + v.venta.montoVenta, 0)
    };
  }) : [];
  const visitsByType = [{
    name: 'Cobro',
    value: chartVisits.filter(v => v.tipoVisita === 'cobro').length,
    color: '#059669'
  }, {
    name: 'Venta',
    value: chartVisits.filter(v => v.tipoVisita === 'venta').length,
    color: '#2952C4'
  }, {
    name: 'Cobro y Venta',
    value: chartVisits.filter(v => v.tipoVisita === 'cobro_y_venta').length,
    color: '#5483df'
  }, {
    name: 'Seguimiento',
    value: chartVisits.filter(v => v.tipoVisita === 'seguimiento').length,
    color: '#d97706'
  }].filter(d => d.value > 0);
  const last7Days = Array.from({
    length: 7
  }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (6 - i));
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const visitsByDay = last7Days.map(day => {
    const dayVisits = chartVisits.filter(v => new Date(v.fecha).toDateString() === day.toDateString());
    return {
      name: day.toLocaleDateString('es-MX', {
        weekday: 'short',
        day: 'numeric'
      }),
      visitas: dayVisits.length,
      cobrado: dayVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0) / 1000
    };
  });
  const barChartRef = useRef<HTMLDivElement>(null);
  const pieChartRef = useRef<HTMLDivElement>(null);
  const [isDownloadingBar, setIsDownloadingBar] = useState(false);
  const [isDownloadingPie, setIsDownloadingPie] = useState(false);
  const CustomTooltip = ({
    active,
    payload,
    label
  }: any) => {
    if (active && payload && payload.length) {
      return <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-3 text-sm" data-id="element-2426" data-mp="1pdx64en3l5oi">
          <p className="font-semibold text-slate-900 mb-1 font-heading" data-id="element-2427" data-mp="1pc2szmi2ihuz">
            {label}
          </p>
          {payload.map((entry: any, index: number) => <p key={index} style={{
          color: entry.color
        }} className="font-medium" data-id="element-2428" data-mp="2i1t2fi8r8vkr" data-mpi={index}>
            
              {entry.name}:{' '}
              {entry.name.includes('cobrad') || entry.name.includes('vendid') ? formatCurrency(entry.value) : entry.value}
            </p>)}
        </div>;
    }
    return null;
  };
  // Cobrado del header:
  // - Sin filtro de fechas -> total global que viene del contexto (misma fuente
  //   y alcance que "Vendido"), no solo las visitas cargadas en memoria.
  // - Con filtro de fechas -> se calcula sobre las visitas filtradas.
  const cobradoDeVisitas = chartVisits.reduce((sum, v) => sum + getEffectiveCobro(v), 0);
  const cobradoGlobal = isAdmin ? totalCobradoCtx : cobradoPorVendedor[user?.id || ''] || 0;
  const totalCobradoGeneral = hasChartFilter ? cobradoDeVisitas : cobradoGlobal || cobradoDeVisitas;
  // Mismo criterio para el conteo de visitas del header.
  const visitasGlobal = isAdmin ? totalVisitasCtx : visitasPorVendedor[user?.id || ''] || 0;
  const totalVisitasGeneral = hasChartFilter ? chartVisits.length : visitasGlobal || allRelevantVisits.length;
  const totalVendidoGeneral = totalVendidoCtx;
  const downloadChartPDF = async (chartRef: React.RefObject<HTMLDivElement>, title: string, setLoading: (v: boolean) => void) => {
    if (!chartRef.current) return;
    setLoading(true);
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false
      });
      const imgData = canvas.toDataURL('image/png');
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      const ml = 12;
      const mr = 12;
      const cw = pw - ml - mr;
      const genDate = new Date().toLocaleDateString('es-DO', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      });
      // ── HEADER BAND (same as reports) ──────────────────────────────────
      const headerH = 36;
      doc.setFillColor(13, 27, 70);
      doc.rect(0, 0, pw, headerH, 'F');
      // Logo
      const logoBase64 = await getLogoBase64();
      if (logoBase64) {
        doc.setFillColor(255, 255, 255);
        doc.roundedRect(ml, 5, 26, 26, 3, 3, 'F');
        doc.addImage(logoBase64, 'PNG', ml + 1, 6, 24, 24);
      }
      // Company name
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(13);
      doc.setFont('helvetica', 'bold');
      doc.text('Miladys Rodriguez & Asoc.', ml + 30, 16);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(123, 163, 216);
      doc.text('Sistema de Control de Vendedores', ml + 30, 23);
      // Date top right
      doc.setFontSize(8);
      doc.setTextColor(123, 163, 216);
      doc.text('Generado: ' + genDate, pw - mr, 16, {
        align: 'right'
      });
      // ── ACCENT BAR ─────────────────────────────────────────────────────
      doc.setFillColor(41, 82, 196);
      doc.rect(0, headerH, pw, 2, 'F');
      // ── TITLE SECTION ──────────────────────────────────────────────────
      let y = headerH + 2;
      const titleStripH = 24;
      doc.setFillColor(248, 250, 255);
      doc.rect(0, y, pw, titleStripH, 'F');
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(0, y + titleStripH, pw, y + titleStripH);
      y += 6;
      doc.setTextColor(13, 27, 70);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title.toUpperCase(), ml, y + 5);
      // Period text
      const periodText = chartDateFrom || chartDateTo ? `Período: ${chartDateFrom || 'Inicio'} al ${chartDateTo || 'Hoy'}` : 'Todos los registros';
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100, 116, 139);
      doc.text(periodText, ml, y + 12);
      // Records pill
      const recordCount = chartVisits.length;
      const pillW = 30;
      doc.setFillColor(41, 82, 196);
      doc.roundedRect(pw - mr - pillW, y, pillW, 8, 2, 2, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'bold');
      doc.text(recordCount + ' registros', pw - mr - pillW / 2, y + 5.5, {
        align: 'center'
      });
      y = headerH + 2 + titleStripH + 2;
      // ── STATS ROW ─────────────────────────────────────────────────────
      const statsH = 18;
      const statW = cw / 5;
      doc.setFillColor(248, 250, 255);
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.rect(ml, y, cw, statsH, 'FD');
      const statsData = [{
        label: 'VISITAS',
        value: String(chartVisits.length),
        r: 13,
        g: 27,
        b: 70
      }, {
        label: 'COMPLETADAS',
        value: String(chartVisits.filter(v => v.estado === 'completada').length),
        r: 13,
        g: 27,
        b: 70
      }, {
        label: 'COBRADO',
        value: formatCurrency(chartVisits.reduce((s, v) => s + getEffectiveCobro(v), 0)),
        r: 5,
        g: 150,
        b: 105
      }, {
        label: 'VENDIDO',
        value: formatCurrency(chartVisits.reduce((s, v) => s + v.venta.montoVenta, 0)),
        r: 41,
        g: 82,
        b: 196
      }, {
        label: 'T. PROMEDIO',
        value: Math.round(chartVisits.filter(v => v.tiempoTotal).reduce((s, v) => s + (v.tiempoTotal || 0), 0) / (chartVisits.filter(v => v.tiempoTotal).length || 1)) + ' min',
        r: 13,
        g: 27,
        b: 70
      }];
      statsData.forEach((stat, i) => {
        const sx = ml + i * statW;
        if (i > 0) {
          doc.setDrawColor(226, 232, 240);
          doc.setLineWidth(0.2);
          doc.line(sx, y + 2, sx, y + statsH - 2);
        }
        doc.setFontSize(10);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(stat.r, stat.g, stat.b);
        doc.text(stat.value, sx + statW / 2, y + 8, {
          align: 'center'
        });
        doc.setFontSize(6.5);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(148, 163, 184);
        doc.text(stat.label, sx + statW / 2, y + 14, {
          align: 'center'
        });
      });
      y += statsH + 5;
      // ── SECTION TITLE ──────────────────────────────────────────────────
      doc.setFontSize(8.5);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(13, 27, 70);
      doc.text('GRÁFICO', ml, y);
      doc.setDrawColor(41, 82, 196);
      doc.setLineWidth(0.6);
      doc.line(ml, y + 1.5, ml + doc.getTextWidth('GRÁFICO'), y + 1.5);
      y += 5;
      // ── CHART IMAGE ────────────────────────────────────────────────────
      const imgW = cw;
      const imgH = canvas.height / canvas.width * imgW;
      const maxH = ph - y - 16;
      const finalH = Math.min(imgH, maxH);
      const finalW = finalH / imgH * imgW;
      // Chart container with border
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(ml, y, finalW, finalH + 4, 2, 2, 'FD');
      doc.addImage(imgData, 'PNG', ml + 2, y + 2, finalW - 4, finalH);
      // ── FOOTER (same as reports) ───────────────────────────────────────
      doc.setFillColor(13, 27, 70);
      doc.rect(0, ph - 9, pw, 9, 'F');
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(74, 111, 165);
      doc.text('Miladys Rodriguez & Asoc. © 2025 — Sistema de Control de Vendedores', ml, ph - 3);
      doc.text('Página 1 de 1', pw - mr, ph - 3, {
        align: 'right'
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      const safeName = title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      doc.save(`grafico-${safeName}-${dateStr}.pdf`);
    } catch (err) {
      console.error('Error al generar PDF del gráfico:', err);
      alert('Error al generar el PDF: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setLoading(false);
    }
  };
  // Delivery orders for repartidor — only empacado
  const deliveryOrders = orders.filter(o => o.estado === 'empacado');
  const deliveredToday = orders.filter(o => o.estado === 'entregado' && new Date(o.fecha).toDateString() === new Date().toDateString());
  const handleMarkDelivered = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'entregado'
    }).eq('id', id);
    await refreshData();
  };
  const handleCancelDelivery = async (id: string) => {
    await supabaseAdmin.from('orders').update({
      estado: 'cancelado'
    }).eq('id', id);
    await refreshData();
  };
  const isVisitsLoading = appLoading && visits.length === 0;
  return <div className="space-y-6" data-id="element-2429" data-mp="1npj1b7x575vu">
      {/* Hero Banner */}
      <div className="rounded-2xl p-4 sm:p-5 lg:p-8 text-white relative overflow-hidden animate-slide-up shadow-lg" style={{
      background: 'linear-gradient(135deg, #0D1B46 0%, #152C6E 50%, #1E3D96 100%)'
    }} data-id="element-2430" data-mp="1jnxhf2m6xrc5">
        
        <div className="absolute inset-0 opacity-5" style={{
        backgroundImage: 'radial-gradient(circle at 20% 50%, #5483df 0%, transparent 50%), radial-gradient(circle at 80% 20%, #2952C4 0%, transparent 40%)'
      }} data-id="element-2431" data-mp="1sq6pzmxwfamw" />
        
        <div className="absolute top-0 right-0 w-72 h-72 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" style={{
        backgroundColor: 'rgba(41,82,196,0.15)'
      }} data-id="element-2432" data-mp="2gd6i0bvalcd7" />
        
        <div className="relative" data-id="element-2433" data-mp="2qc46jespx5w7">
          <h2 className="text-lg sm:text-xl lg:text-3xl font-bold font-heading" data-id="element-2434" data-mp="2ld19ohmw65zb">
            ¡Buen día, {user?.nombre.split(' ')[0]}!
          </h2>
          <p className="mt-1 text-blue-200 text-xs sm:text-sm" data-id="element-2435" data-mp="2shk9r448szuc">
            {isAdmin ? 'Resumen de actividad de tu equipo comercial.' : 'Tu resumen de actividad del día.'}
          </p>
          <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:gap-3 lg:flex lg:items-center lg:gap-10" data-id="element-2436" data-mp="32cubrrun5784">
            {canSeeCollections && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-2437" data-mp="3c4hwl9wozq0m">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-2438" data-mp="3g50ot52hyy3c">
                  Cobrado
                </p>
                <p className="text-xs sm:text-lg lg:text-3xl font-bold text-emerald-400 leading-tight mt-0.5 truncate" data-id="element-2439" data-mp="3l56eifp7iais">
                  {isVisitsLoading ? <span className="inline-block w-16 sm:w-24 h-4 sm:h-6 lg:h-9 bg-white/10 rounded animate-pulse" data-id="element-2440" data-mp="1qr5cjlyd17eh" /> : formatCurrency(totalCobradoGeneral)}
                </p>
              </div>}
            {canSeeSales && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-2441" data-mp="1o80njttvunkx">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-2442" data-mp="1ege2l5rqta4z">
                  Vendido
                </p>
                <p className="text-xs sm:text-lg lg:text-3xl font-bold text-blue-300 leading-tight mt-0.5 truncate" data-id="element-2443" data-mp="19ickoo61zgv3">
                  {formatCurrency(totalVendidoGeneral)}
                </p>
              </div>}
            {canSeeVisits && <div className="bg-white/10 rounded-lg sm:rounded-xl p-2 sm:p-3 lg:bg-transparent lg:p-0" data-id="element-2444" data-mp="2244dvc1mybs4">
                <p className="text-blue-300 text-[10px] sm:text-xs lg:text-sm leading-tight" data-id="element-2445" data-mp="1s7sz8fekp9ui">
                  Visitas
                </p>
                <p className="text-xs sm:text-lg lg:text-3xl font-bold text-white leading-tight mt-0.5" data-id="element-2446" data-mp="lsryzxiim5i4">
                  {isVisitsLoading ? <span className="inline-block w-8 sm:w-12 h-4 sm:h-6 lg:h-9 bg-white/10 rounded animate-pulse" data-id="element-2447" data-mp="guqjtno7mqbx" /> : totalVisitasGeneral}
                </p>
              </div>}
          </div>
        </div>
      </div>

      {/* ═══ DELIVERY SECTION (visible for anyone with dashboard_entregas) ═══ */}
      {canSeeEntregas && <>
          {/* Delivery Stats */}
          <div className="grid grid-cols-3 sm:grid-cols-3 gap-2 sm:gap-4 animate-stagger-1" data-id="element-2448" data-mp="2odb61xsl8rsn">
            <StatsCard title="Por Entregar" value={deliveryOrders.length} icon={<PackageIcon className="w-6 h-6" data-id="element-2450" data-mp="2ef56hmu97xqy" />} variant="warning" data-id="element-2449" data-mp="2obggu7aaadir" />
          
            <StatsCard title="Entregados Hoy" value={deliveredToday.length} icon={<CheckCircleIcon className="w-6 h-6" data-id="element-2452" data-mp="12zybhfwmqqs9" />} variant="success" data-id="element-2451" data-mp="2ehhizkh8tfwm" />
          
            <StatsCard title="Total Pedidos" value={orders.filter(o => o.estado !== 'cancelado').length} icon={<TruckIcon className="w-6 h-6" data-id="element-2454" data-mp="3m610p94v5we3" />} variant="primary" data-id="element-2453" data-mp="132azoaszdxdr" />
          
          </div>

          {/* Delivery Orders List */}
          <Card padding="none" data-id="element-2455" data-mp="3h9ttze4n8gv3">
            <div className="px-4 sm:px-6 py-3 sm:py-5 border-b border-slate-100 flex justify-between items-center gap-2" data-id="element-2456" data-mp="3v78m4qma72qr">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-2457" data-mp="3r5nblbx7z0jq">
                <div className="p-1.5 sm:p-2 bg-amber-50 rounded-lg flex-shrink-0" data-id="element-2458" data-mp="33g2tev8w47bi">
                  <TruckIcon className="w-4 sm:w-5 h-4 sm:h-5 text-amber-600" data-id="element-2459" data-mp="2yh8214wuxafs" />
                </div>
                <div className="min-w-0" data-id="element-2460" data-mp="3c8mknamur9mb">
                  <h3 className="font-bold text-slate-900 font-heading text-sm sm:text-base" data-id="element-2461" data-mp="3c9o7ecd8dkjv">
                    Pedidos Listos para Entrega
                  </h3>
                  <p className="text-xs sm:text-sm text-slate-500 hidden sm:block" data-id="element-2462" data-mp="2js3i9jaeyy5k">
                    Pedidos empacados listos para entregar
                  </p>
                </div>
              </div>
              {newOrderAlert && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 border border-amber-200 rounded-full text-[10px] sm:text-xs font-bold text-amber-700 animate-pulse flex-shrink-0" data-id="element-2463" data-mp="ip2kvyv1qgdc">
                  <BellIcon className="w-3 sm:w-3.5 h-3 sm:h-3.5" data-id="element-2464" data-mp="sgp68o13lvtt" />
                  <span className="hidden sm:inline" data-id="element-2465" data-mp="xdys5tpa3pcw">
                    Nuevo pedido empacado
                  </span>
                  <span className="sm:hidden" data-id="element-2466" data-mp="3vy74c3r2joh1">Nuevo</span>
                </span>}
            </div>
            <div className="divide-y divide-slate-100" data-id="element-2467" data-mp="4ql3eivqi03v">
              {deliveryOrders.length === 0 ? <div className="px-4 sm:px-6 py-8 sm:py-12 text-center animate-fade-in" data-id="element-2468" data-mp="9o4ifl09agwv">
                  <CheckCircleIcon className="w-8 sm:w-10 h-8 sm:h-10 text-emerald-300 mx-auto mb-2" data-id="element-2469" data-mp="eq3lets33bta" />
                  <p className="text-xs sm:text-sm text-slate-400 font-medium" data-id="element-2470" data-mp="1l4b9twabt0go">
                    Sin entregas pendientes
                  </p>
                  <p className="text-[10px] sm:text-xs text-slate-400 mt-1" data-id="element-2471" data-mp="3algif9oandbg">
                    ¡Buen trabajo!
                  </p>
                </div> : deliveryOrders.map(order => <div key={order.id} className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-2472" data-mp="30vncao6x7qel">
              
                    <div className="flex-1 min-w-0" data-id="element-2473" data-mp="2qy8v2md6axl8">
                      <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-2474" data-mp="2qvmn4lmdeg2h">
                        <h4 className="font-semibold text-slate-900 truncate font-heading text-sm sm:text-base" data-id="element-2475" data-mp="2mvck9htvk5ru">
                          {order.farmacia}
                        </h4>
                        <Badge variant={order.estado === 'empacado' ? 'info' : 'warning'} data-id="element-2476" data-mp="2cyzrcm65lvy5">
                    
                          {order.estado === 'empacado' ? 'Empacado' : 'Enviado'}
                        </Badge>
                      </div>
                      <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate" data-id="element-2477" data-mp="3g7y42970kqyf">
                        {new Date(order.fecha).toLocaleDateString('es-DO', {
                  day: 'numeric',
                  month: 'short'
                })}
                        {' · '}
                        <span className="font-medium text-slate-700" data-id="element-2478" data-mp="3belpkrw1u0je">
                          {formatCurrency(order.total)}
                        </span>
                        {' · '}
                        {order.items} items
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" data-id="element-2479" data-mp="2nir5593ndzcn">
                      <button onClick={() => handleMarkDelivered(order.id)} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] sm:text-xs font-semibold rounded-lg transition-colors" data-id="element-2480" data-mp="2sgit5s1691bk">
                  
                        <CheckIcon className="w-3.5 h-3.5" data-id="element-2481" data-mp="2b76v2askuatz" />
                        <span className="hidden sm:inline" data-id="element-2482" data-mp="2b5na46vz7blv">Entregado</span>
                      </button>
                      <button onClick={() => handleCancelDelivery(order.id)} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-[10px] sm:text-xs font-semibold rounded-lg transition-colors" data-id="element-2483" data-mp="266slupf8ouud">
                  
                        <BanIcon className="w-3.5 h-3.5" data-id="element-2484" data-mp="2u02fce8vs0i5" />
                        <span className="hidden sm:inline" data-id="element-2485" data-mp="2pvun4737s2f6">Cancelar</span>
                      </button>
                    </div>
                  </div>)}
            </div>
            {/* Recent deliveries */}
            {deliveredToday.length > 0 && <div className="px-4 sm:px-6 py-3 border-t border-slate-100 bg-slate-50/50" data-id="element-2486" data-mp="19hup51nximuk">
                <p className="text-xs font-semibold text-slate-500 mb-2" data-id="element-2487" data-mp="14jt6ghuf0pjm">
                  Entregas de hoy
                </p>
                <div className="space-y-1.5" data-id="element-2488" data-mp="dgf9xgi12b0e">
                  {deliveredToday.slice(0, 5).map(order => <div key={order.id} className="flex items-center justify-between text-sm" data-id="element-2489" data-mp="9h6co1ns3v03">
                
                      <span className="text-slate-600 truncate" data-id="element-2490" data-mp="3vvhek7kjm6vw">
                        {order.farmacia} · {order.numeroPedido}
                      </span>
                      <Badge variant="success" data-id="element-2491" data-mp="3qx6886tgieif">Entregado</Badge>
                    </div>)}
                </div>
              </div>}
          </Card>
        </>}

      {/* ═══ STANDARD DASHBOARD SECTIONS ═══ */}
      {!isRepartidor ? <>
          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4 animate-stagger-1" data-id="element-2492" data-mp="3cy6famywvdgg">
            {canSeeVisits && <StatsCard title="Visitas Hoy" value={isVisitsLoading ? '...' : stats.totalVisitas} subtitle={isVisitsLoading ? 'Cargando...' : `${stats.visitasCompletadas} completadas`} icon={<MapPinIcon className="w-6 h-6" data-id="element-2494" data-mp="1nm04sarnqysk" />} variant="primary" data-id="element-2493" data-mp="1g1z6fpk280m7" />}
            {canSeeCollections && <StatsCard title="Cobrado Hoy" value={isVisitsLoading ? '...' : formatCurrency(stats.totalCobrado)} icon={<DollarSignIcon className="w-6 h-6" data-id="element-2496" data-mp="zrgnqqosi3ce" />} variant="success" data-id="element-2495" data-mp="1xdo0woz5t40d" />}
            {canSeeSales && <StatsCard title="Vendido Hoy" value={isVisitsLoading ? '...' : formatCurrency(stats.totalVendido)} icon={<ShoppingCartIcon className="w-6 h-6" data-id="element-2498" data-mp="1dr8613mca5w7" />} variant="warning" data-id="element-2497" data-mp="14qthzwhzqcp0" />}
            {canSeeVisits && <StatsCard title="Tiempo Promedio" value={isVisitsLoading ? '...' : `${stats.tiempoPromedio} min`} subtitle={isVisitsLoading ? 'Cargando...' : 'por visita'} icon={<ClockIcon className="w-6 h-6" data-id="element-2500" data-mp="q1wnrkmcu6me" />} variant="default" data-id="element-2499" data-mp="2l5b8bngthmc9" />}
          </div>

          {/* Charts date filter + row */}
          {canSeeCharts && <div className="space-y-3 animate-stagger-2" data-id="element-2501" data-mp="yzyxqpo2g9lt">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3" data-id="element-2502" data-mp="u1fckrhar1ui">
                <div className="flex items-center gap-2" data-id="element-2503" data-mp="6cw4od25sr2y">
                  <div className="p-1.5 bg-[#eef2fc] rounded-lg" data-id="element-2504" data-mp="2arddkz6o6jh">
                    <CalendarIcon className="w-4 h-4 text-[#2952C4]" data-id="element-2505" data-mp="28ekwdvsuds6" />
                  </div>
                  <h3 className="text-xs sm:text-sm font-semibold text-slate-700" data-id="element-2506" data-mp="g3787yhxky9b">
                    Rango de fechas
                  </h3>
                  {hasChartFilter && <span className="text-[10px] sm:text-xs text-[#2952C4] bg-[#eef2fc] px-1.5 sm:px-2 py-0.5 rounded-full font-medium" data-id="element-2507" data-mp="3jpoc71kuxtdz">
                      Filtrado
                    </span>}
                </div>
                <div className="flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto" data-id="element-2508" data-mp="1u7r627tqf8qa">
                  <div className="flex items-center gap-1 flex-1 sm:flex-initial" data-id="element-2509" data-mp="1z1vbunr9haps">
                    <label className="text-[10px] sm:text-xs text-slate-500 font-medium whitespace-nowrap" data-id="element-2510" data-mp="1lagqia9vsxfk">
                      Desde
                    </label>
                    <input type="date" value={chartDateFrom} onChange={e => setChartDateFrom(e.target.value)} className="w-full sm:w-auto px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-2511" data-mp="2dxbc5aifq2um" />
                
                  </div>
                  <div className="flex items-center gap-1 flex-1 sm:flex-initial" data-id="element-2512" data-mp="sjgqeaeo8dt6">
                    <label className="text-[10px] sm:text-xs text-slate-500 font-medium whitespace-nowrap" data-id="element-2513" data-mp="xjuwvo0mjfp3">
                      Hasta
                    </label>
                    <input type="date" value={chartDateTo} onChange={e => setChartDateTo(e.target.value)} className="w-full sm:w-auto px-2 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-2514" data-mp="1cv2lctphv55s" />
                
                  </div>
                  {hasChartFilter && <button onClick={() => {
              setChartDateFrom('');
              setChartDateTo('');
            }} className="p-1 sm:p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded-lg transition-colors flex-shrink-0" title="Limpiar filtro" data-id="element-2515" data-mp="1rrtqzkprhd3u">
                
                      <FilterXIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-2516" data-mp="6d5sn30cy4cc" />
                    </button>}
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6" data-id="element-2517" data-mp="3sg6gavbcopml">
                {(isAdmin || canSeeCollections || canSeeSales) && <Card data-id="element-2518" data-mp="67etw06ya3nq">
                    <div className="flex items-start justify-between gap-2" data-id="element-2519" data-mp="6753ibhztuhi">
                      <CardHeader title={isAdmin ? 'Rendimiento por Vendedor' : 'Actividad Últimos 7 Días'} subtitle={isAdmin ? `Cobros y ventas${hasChartFilter ? ' (filtrado)' : ' acumulados'}` : `Visitas y cobros${hasChartFilter ? ' (filtrado)' : ' diarios'}`} data-id="element-2520" data-mp="axvblwht0bfw" />
                
                      <button onClick={() => downloadChartPDF(barChartRef, isAdmin ? 'Rendimiento por Vendedor' : 'Actividad Últimos 7 Días', setIsDownloadingBar)} disabled={isDownloadingBar} className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold rounded-lg transition-colors flex-shrink-0 bg-slate-100 text-slate-600 hover:bg-[#eef2fc] hover:text-[#2952C4] disabled:opacity-50 disabled:cursor-not-allowed" title="Descargar gráfico como PDF" data-id="element-2521" data-mp="tuwt2gsjo40q">
                  
                        {isDownloadingBar ? <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-2522" data-mp="31meeesr02gp0" /> : <DownloadIcon className="w-3.5 h-3.5" data-id="element-2523" data-mp="3gbsk9qvdafzn" />}
                        <span className="hidden sm:inline" data-id="element-2524" data-mp="3bek3nupvak0i">PDF</span>
                      </button>
                    </div>
                    <div className="mt-4 sm:mt-6 h-48 lg:h-64" ref={barChartRef} data-id="element-2525" data-mp="3pda3hi42mxsk">
                
                      <ResponsiveContainer width="100%" height="100%" data-id="element-2526" data-mp="7ukwi29f81z4">
                        {isAdmin ? <BarChart data={cobrosBarData} barGap={4} data-id="element-2527" data-mp="ct5zg9g24zr9">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-2528" data-mp="crbmpiexwdy9" />
                    
                            <XAxis dataKey="name" tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-2529" data-mp="3rybak799faw" />
                    
                            <YAxis tick={{
                    fontSize: 11,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} data-id="element-2530" data-mp="3c9kq9r21q4ov" />
                    
                            <Tooltip content={<CustomTooltip data-id="element-2532" data-mp="3mqeqrfel2pax" />} data-id="element-2531" data-mp="3mrpx9mzeyb5q" />
                            <Bar dataKey="cobrado" name="Cobrado" fill="#059669" radius={[6, 6, 0, 0]} data-id="element-2533" data-mp="3mp3ndgp1vjkc" />
                    
                            <Bar dataKey="vendido" name="Vendido" fill="#2952C4" radius={[6, 6, 0, 0]} data-id="element-2534" data-mp="38ryn5xc0pex0" />
                    
                          </BarChart> : <BarChart data={visitsByDay} barGap={4} data-id="element-2535" data-mp="2z13rk2cvt2yw">
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-2536" data-mp="2u2aj4ocgc25l" />
                    
                            <XAxis dataKey="name" tick={{
                    fontSize: 12,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-2537" data-mp="2q31q5lp8k8b3" />
                    
                            <YAxis tick={{
                    fontSize: 11,
                    fill: '#64748b'
                  }} axisLine={false} tickLine={false} data-id="element-2538" data-mp="vn31q98idscz" />
                    
                            <Tooltip content={<CustomTooltip data-id="element-2540" data-mp="19jypl7tf5od3" />} data-id="element-2539" data-mp="19jgmpsc94bw7" />
                            <Bar dataKey="visitas" name="Visitas" fill="#2952C4" radius={[6, 6, 0, 0]} data-id="element-2541" data-mp="cv0u1nduhmev" />
                    
                          </BarChart>}
                      </ResponsiveContainer>
                    </div>
                  </Card>}

                {canSeeVisits && <Card data-id="element-2542" data-mp="3q9x68w29nrr8">
                    <div className="flex items-start justify-between gap-2" data-id="element-2543" data-mp="3q8m0wrt6aby0">
                      <CardHeader title="Visitas por Tipo" subtitle={`Distribución de actividades${hasChartFilter ? ' (filtrado)' : ''}`} data-id="element-2544" data-mp="3ua7b25eboc4i" />
                
                      <button onClick={() => downloadChartPDF(pieChartRef, 'Visitas por Tipo', setIsDownloadingPie)} disabled={isDownloadingPie} className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold rounded-lg transition-colors flex-shrink-0 bg-slate-100 text-slate-600 hover:bg-[#eef2fc] hover:text-[#2952C4] disabled:opacity-50 disabled:cursor-not-allowed" title="Descargar gráfico como PDF" data-id="element-2545" data-mp="2du0bn8cd18dj">
                  
                        {isDownloadingPie ? <div className="w-3.5 h-3.5 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-2546" data-mp="17634pxkmywom" /> : <DownloadIcon className="w-3.5 h-3.5" data-id="element-2547" data-mp="1c4ms82evogj3" />}
                        <span className="hidden sm:inline" data-id="element-2548" data-mp="14ldi26yjsdsz">PDF</span>
                      </button>
                    </div>
                    <div className="mt-4 sm:mt-6 h-48 lg:h-64" ref={pieChartRef} data-id="element-2549" data-mp="zunaqo6ka0vn">
                
                      <ResponsiveContainer width="100%" height="100%" data-id="element-2550" data-mp="uwdjuprvdmfu">
                        <PieChart data-id="element-2551" data-mp="pxsgihhbmmn6">
                          <Pie data={visitsByType} cx="50%" cy="50%" innerRadius={45} outerRadius={70} paddingAngle={4} dataKey="value" data-id="element-2552" data-mp="pyk8zjfmg498">
                      
                            {visitsByType.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-2553" data-mp="19jyhw59w0vgw" data-mpi={index} />)}
                          </Pie>
                          <Tooltip formatter={(value: number, name: string) => [`${value} visitas`, name]} contentStyle={{
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    fontSize: '13px'
                  }} data-id="element-2554" data-mp="25264cp2gbf0" />
                    
                          <Legend verticalAlign="bottom" height={30} formatter={value => <span className="text-xs sm:text-sm text-slate-600" data-id="element-2556" data-mp="3d026p7xe59jo">
                                {value}
                              </span>} data-id="element-2555" data-mp="3qsrsbb90ccg9" />
                    
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </Card>}
              </div>
            </div>}

          {/* Main content grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 animate-stagger-3" data-id="element-2557" data-mp="2u4vhkn094bcy">
            <div className="lg:col-span-2 space-y-4 sm:space-y-6" data-id="element-2558" data-mp="2y6grq0leibjg">
              {canSeeVisits && <Card padding="none" data-id="element-2559" data-mp="2fi6a9yzrctw0">
                  <div className="px-4 sm:px-6 py-3 sm:py-5 border-b border-slate-100 flex justify-between items-center" data-id="element-2560" data-mp="2ahilzl2wyr5h">
                    <CardHeader title="Visitas Recientes" subtitle="Últimas actividades registradas" data-id="element-2561" data-mp="2ohtbokshlzp2" />
                
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-2562" data-mp="2yeehh8cbzety">
                    {recentVisits.length === 0 ? <div className="p-8 sm:p-12 text-center animate-fade-in" data-id="element-2563" data-mp="2tgmr4irc40rv">
                        <div className="inline-flex items-center justify-center w-12 sm:w-16 h-12 sm:h-16 bg-slate-100 rounded-2xl mb-3 sm:mb-4" data-id="element-2564" data-mp="376zzgm851hhq">
                          <MapPinIcon className="w-6 sm:w-8 h-6 sm:h-8 text-slate-400" data-id="element-2565" data-mp="3366arqmvfyns" />
                        </div>
                        <p className="text-slate-500 font-medium text-sm" data-id="element-2566" data-mp="3h3bevl30ynge">
                          No hay visitas registradas
                        </p>
                      </div> : recentVisits.map(visit => <div key={visit.id} className="px-3 sm:px-6 py-2.5 sm:py-4 hover:bg-slate-50/50 transition-colors" data-id="element-2567" data-mp="44myrmmveoxr">
                  
                          <div className="flex items-start justify-between gap-2" data-id="element-2568" data-mp="8xg3wbm93zad">
                            <div className="flex-1 min-w-0" data-id="element-2569" data-mp="dtoerctf16bq">
                              <div className="flex items-center gap-1.5 sm:gap-2" data-id="element-2570" data-mp="3dad1eyn4rcmy">
                                <h4 className="font-semibold text-slate-900 font-heading text-sm sm:text-base truncate" data-id="element-2571" data-mp="3han3i07sxiwj">
                                  {visit.clienteNombre}
                                </h4>
                                {getStatusBadge(visit.estado)}
                              </div>
                              <p className="text-xs sm:text-sm text-slate-500 mt-0.5 sm:mt-1 truncate" data-id="element-2572" data-mp="11kttfonmdskg">
                                {isAdmin && <span className="font-medium" data-id="element-2573" data-mp="16halgtxxwr2t">
                                    {visit.vendedorNombre} ·{' '}
                                  </span>}
                                {visit.zona} ·{' '}
                                {getVisitTypeLabel(visit.tipoVisita)}
                              </p>
                              <div className="flex items-center gap-2 sm:gap-4 mt-1 sm:mt-2 text-xs sm:text-sm flex-wrap" data-id="element-2574" data-mp="2cct73q9s91d5">
                                <span className="text-slate-500" data-id="element-2575" data-mp="28bg19g6owy68">
                                  <ClockIcon className="w-3 sm:w-4 h-3 sm:h-4 inline mr-0.5 sm:mr-1" data-id="element-2576" data-mp="23b262yq6tipm" />
                                  {formatTime(visit.horaLlegada)}
                                  {visit.tiempoTotal && <span className="ml-1 text-slate-400" data-id="element-2577" data-mp="1tegzi8yirzjo">
                                      ({visit.tiempoTotal}m)
                                    </span>}
                                </span>
                                {canSeeCollections && visit.cobro.huboCobo && ((visit.cobro as any)?.anulado ? <span className="inline-flex items-center gap-1 text-slate-400 font-semibold text-[10px] sm:text-xs line-through" data-id="element-2578" data-mp="1qt95oqo8di1c">
                                      {formatCurrency(visit.cobro.montoCobrado)}
                                      <span className="no-underline ml-0.5 px-1 py-0.5 bg-slate-100 text-slate-500 rounded text-[9px] sm:text-[10px] font-bold" data-id="element-2579" data-mp="1h3fwtxfh3h0s">
                                        ANULADO
                                      </span>
                                    </span> : <span className="text-emerald-600 font-semibold text-xs sm:text-sm" data-id="element-2580" data-mp="1vratfowlq5j5">
                                      {formatCurrency(visit.cobro.montoCobrado)}
                                    </span>)}
                                {canSeeSales && visit.venta.huboVenta && <span className="font-semibold text-xs sm:text-sm" style={{
                        color: '#2952C4'
                      }} data-id="element-2581" data-mp="oftv6upt2mgk">
                          
                                    {formatCurrency(visit.venta.montoVenta)}
                                  </span>}
                              </div>
                            </div>
                          </div>
                        </div>)}
                  </div>
                </Card>}

              {/* Pending Orders Widget - only if user has pedidos permission */}
              {canSeePedidos && <Card padding="none" data-id="element-2582" data-mp="7w4m833og0rx">
                  <div className="px-4 sm:px-6 py-3 sm:py-5 border-b border-slate-100 flex justify-between items-center gap-2" data-id="element-2583" data-mp="33bc0zpfw08g">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0" data-id="element-2584" data-mp="3bhtsnptg4o3o">
                      <div className="p-1.5 sm:p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-2585" data-mp="36j8nrd391g8w">
                        <TruckIcon className="w-4 sm:w-5 h-4 sm:h-5 text-blue-600" data-id="element-2586" data-mp="2l1s0phy0lwy8" />
                      </div>
                      <div className="min-w-0" data-id="element-2587" data-mp="2q0uxkf3ucwv4">
                        <h3 className="font-bold text-slate-900 font-heading text-sm sm:text-base" data-id="element-2588" data-mp="2uz67epu4z7dc">
                          Pedidos Pendientes
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 hidden sm:block" data-id="element-2589" data-mp="2z22xf1m0a5rb">
                          Aprobar o cancelar órdenes
                        </p>
                      </div>
                    </div>
                    {newOrderAlert && <span className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 bg-amber-100 border border-amber-200 rounded-full text-[10px] sm:text-xs font-bold text-amber-700 animate-pulse flex-shrink-0" data-id="element-2590" data-mp="3dpe7t06gbjpu">
                        <BellIcon className="w-3 sm:w-3.5 h-3 sm:h-3.5" data-id="element-2591" data-mp="3mrx93ful9o9z" />
                        <span className="hidden sm:inline" data-id="element-2592" data-mp="3rqgw7jkx563x">Nuevo pedido</span>
                        <span className="sm:hidden" data-id="element-2593" data-mp="1g2lfmqbe01io">Nuevo</span>
                      </span>}
                  </div>
                  <div className="divide-y divide-slate-100" data-id="element-2594" data-mp="1b27iw4f8kdzy">
                    {pendingOrders.length === 0 ? <div className="px-4 sm:px-6 py-8 sm:py-10 text-center animate-fade-in" data-id="element-2595" data-mp="171xciqnj7lkk">
                        <CheckCircleIcon className="w-8 sm:w-10 h-8 sm:h-10 text-emerald-300 mx-auto mb-2" data-id="element-2596" data-mp="123vixbshc3uu" />
                        <p className="text-xs sm:text-sm text-slate-400 font-medium" data-id="element-2597" data-mp="1yrs2mpj7rfaa">
                          Sin pedidos pendientes
                        </p>
                      </div> : pendingOrders.slice(0, 5).map(order => <div key={order.id} className="px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2 sm:gap-4 hover:bg-slate-50/50 transition-colors" data-id="element-2598" data-mp="1l1et4iqobsiu">
                  
                          <div className="flex-1 min-w-0" data-id="element-2599" data-mp="2dosvwxh1ziav">
                            <h4 className="font-semibold text-slate-900 truncate font-heading text-sm sm:text-base" data-id="element-2600" data-mp="25jmhdgirc6wn">
                              {order.farmacia}
                            </h4>
                            <p className="text-xs sm:text-sm text-slate-500 mt-0.5 truncate" data-id="element-2601" data-mp="2fhsfro5cuuwr">
                              {new Date(order.fecha).toLocaleDateString('es-DO', {
                      day: 'numeric',
                      month: 'short'
                    })}
                              {' · '}
                              <span className="font-medium text-slate-700" data-id="element-2602" data-mp="2y8a5rkyszcef">
                                {formatCurrency(order.total)}
                              </span>
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0" data-id="element-2603" data-mp="387ix062aacx3">
                            <button onClick={() => handleApproveOrder(order.id)} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-[10px] sm:text-xs font-semibold rounded-lg transition-colors" data-id="element-2604" data-mp="338600shp7pgs">
                      
                              <CheckCircleIcon className="w-3.5 h-3.5" data-id="element-2605" data-mp="3cu78i8nq1lsy" />
                              <span className="hidden sm:inline" data-id="element-2606" data-mp="3htk1zc93lr4i">Aprobar</span>
                            </button>
                            <button onClick={() => handleCancelOrder(order.id)} className="flex items-center gap-1 px-2 sm:px-3 py-1.5 bg-rose-100 hover:bg-rose-200 text-rose-700 text-[10px] sm:text-xs font-semibold rounded-lg transition-colors" data-id="element-2607" data-mp="3344fw9kii9v5">
                      
                              <XCircleIcon className="w-3.5 h-3.5" data-id="element-2608" data-mp="2z43qxuzseg9u" />
                              <span className="hidden sm:inline" data-id="element-2609" data-mp="2k61lff973kpy">Cancelar</span>
                            </button>
                          </div>
                        </div>)}
                  </div>
                  {orders.filter(o => o.estado !== 'pendiente').slice(0, 3).length > 0 && <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100 bg-slate-50/50" data-id="element-2610" data-mp="218hzwzrbav22">
                      <p className="text-xs sm:text-sm font-semibold text-slate-500 mb-2" data-id="element-2611" data-mp="1wb9emqavaawl">
                        Recientes
                      </p>
                      <div className="space-y-1.5" data-id="element-2612" data-mp="2p06tskjs7rl4">
                        {orders.filter(o => o.estado !== 'pendiente').slice(0, 3).map(order => <div key={order.id} className="flex items-center justify-between text-sm" data-id="element-2613" data-mp="2f0y1rx8h8n1e">
                    
                              <span className="text-slate-600 truncate" data-id="element-2614" data-mp="2a4qv2289b7ie">
                                {order.farmacia} · {order.numeroPedido}
                              </span>
                              <Badge variant={order.estado === 'entregado' ? 'success' : order.estado === 'cancelado' ? 'danger' : order.estado === 'enviado' ? 'info' : 'warning'} data-id="element-2615" data-mp="ylwmw6phvqr5">
                      
                                {order.estado.charAt(0).toUpperCase() + order.estado.slice(1)}
                              </Badge>
                            </div>)}
                      </div>
                    </div>}
                </Card>}
            </div>

            <div className="space-y-6" data-id="element-2616" data-mp="bsxeus18r0vn">
              {canSeeEquipo && <Card data-id="element-2617" data-mp="bs5uyeh0arl7">
                  <CardHeader title="Equipo Comercial" data-id="element-2618" data-mp="28l7epwn8b2jq" />
                  <div className="mt-4 space-y-3" data-id="element-2619" data-mp="28ms8vl5msjh2">
                    <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl" data-id="element-2620" data-mp="2dk26ho36c1fz">
                      <div className="flex items-center gap-3" data-id="element-2621" data-mp="7vrscoh0fb6a">
                        <UsersIcon className="w-5 h-5 text-slate-500" data-id="element-2622" data-mp="7y4ihowx8ruf" />
                        <span className="text-sm text-slate-600" data-id="element-2623" data-mp="2w5ce79srgts">
                          Vendedores Activos
                        </span>
                      </div>
                      <span className="font-bold text-slate-900" data-id="element-2624" data-mp="cul70k819881">
                        {activeVendors.length}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl" data-id="element-2625" data-mp="3l8k0nf7j8hid">
                      <div className="flex items-center gap-3" data-id="element-2626" data-mp="3ga0ebdp113ph">
                        <CheckCircleIcon className="w-5 h-5 text-emerald-600" data-id="element-2627" data-mp="3u4szosrlle3z" />
                        <span className="text-sm text-slate-600" data-id="element-2628" data-mp="3pbgoplfud5tp">
                          Visitas Completadas
                        </span>
                      </div>
                      <span className="font-bold text-emerald-600" data-id="element-2629" data-mp="2xllm4n2z3jn3">
                        {stats.visitasCompletadas}
                      </span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl" data-id="element-2630" data-mp="2nfc0egmpfmcc">
                      <div className="flex items-center gap-3" data-id="element-2631" data-mp="2sa84wg94o26c">
                        <AlertCircleIcon className="w-5 h-5 text-amber-600" data-id="element-2632" data-mp="2dkk198dh4rfg" />
                        <span className="text-sm text-slate-600" data-id="element-2633" data-mp="2ihivrs11v0e6">
                          En Progreso
                        </span>
                      </div>
                      <span className="font-bold text-amber-600" data-id="element-2634" data-mp="36624g8o0hf6s">
                        {stats.visitasEnProgreso}
                      </span>
                    </div>
                  </div>
                </Card>}

              {canSeeProductividad && <Card data-id="element-2635" data-mp="3178o88hcba2x">
                  <CardHeader title="Productividad" data-id="element-2636" data-mp="31awhd6ivhuh4" />
                  <div className="mt-4" data-id="element-2637" data-mp="1ktiikxakodr6">
                    <div className="flex items-center justify-between mb-2" data-id="element-2638" data-mp="1kwo82dn45hnf">
                      <span className="text-sm text-slate-500" data-id="element-2639" data-mp="1pv7wqlt3d7jh">
                        Meta diaria
                      </span>
                      <span className="text-sm font-semibold text-slate-900" data-id="element-2640" data-mp="3si4tbnbshu0i">
                        {stats.totalVisitas} / 8 visitas
                      </span>
                    </div>
                    <div className="w-full bg-slate-200 rounded-full h-3" data-id="element-2641" data-mp="3ioflup7ph5kl">
                      <div className="h-3 rounded-full transition-all duration-500" style={{
                  width: `${Math.min(stats.totalVisitas / 8 * 100, 100)}%`,
                  backgroundColor: '#2952C4'
                }} data-id="element-2642" data-mp="3dqmcxof0pxgm" />
                  
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-sm" data-id="element-2643" data-mp="2ztheoaljq2vx">
                      <TrendingUpIcon className="w-4 h-4 text-emerald-500" data-id="element-2644" data-mp="2uu4j91glznhq" />
                      <span className="text-slate-600" data-id="element-2645" data-mp="xz8de1ndpedj">
                        {stats.totalVisitas >= 8 ? '¡Meta alcanzada!' : `Faltan ${8 - stats.totalVisitas} visitas`}
                      </span>
                    </div>
                  </div>
                </Card>}

              {stats.visitasEnProgreso > 0 && <Card className="border-amber-200 bg-amber-50" data-id="element-2646" data-mp="1aasn2yujf0vo">
                  <div className="flex items-start gap-3" data-id="element-2647" data-mp="1acluwhgvzr59">
                    <div className="p-2 bg-amber-100 rounded-xl" data-id="element-2648" data-mp="1f8kbvjzvy6vn">
                      <MapPinIcon className="w-5 h-5 text-amber-600" data-id="element-2649" data-mp="hm3hamije7dl" />
                    </div>
                    <div data-id="element-2650" data-mp="mkyc6mts3me2">
                      <h4 className="font-semibold text-slate-900" data-id="element-2651" data-mp="mk51jarp8cqn">
                        Visita en Progreso
                      </h4>
                      <p className="text-sm text-slate-600 mt-1" data-id="element-2652" data-mp="riq2jcerz0g5">
                        Tienes una visita activa. Recuerda finalizarla al salir
                        del cliente.
                      </p>
                    </div>
                  </div>
                </Card>}
            </div>
          </div>
        </> : null}
    </div>;
}